import React, { useState, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    IconOverview,
    IconInvoice,
    IconInbox,
    IconRowActions,
    IconNew,
    IconPortal,
    IconClients,
    IconMessages,
} from '../../icons';
import type { NavContext, NavRole } from '../../config/navigation';
import { NAV_ACTIONS } from '../../config/navigation';
import { canUseInbound } from '../../config/inboundAccess';
import { UNIFIED_INBOX_ENABLED } from '../../config/unifiedInboxAccess';
import { canUseMessaging } from '../../config/messagingAccess';
import { canUseKanzleiCockpit } from '../../config/kanzleiAccess';
import { useInboxNewCount } from '../../hooks/useInboxNewCount';
import { MobileBottomSheet } from '../common/MobileBottomSheet';

/**
 * MobileTabBar — persistente Bottom-Navigation für Mobil/Tablet (M-UX-6.1,
 * `plan-ux-6.1-mobile-tabs.md` §2). Fail-closed hinter
 * `REACT_APP_UX_MOBILE_TABS_ENABLED` + `UX_SHELL_ENABLED` + `!isDesktop` — die
 * Render-/Mount-Entscheidung trifft der Consumer (`MainLayout`, E1), damit der
 * teure `useInboxNewCount`-Read nie auf Desktop mountet.
 *
 * Zwei Rollen-Layouts (§2.2):
 *   - Owner: Übersicht · Rechnungen · [+]-FAB · Posteingang(Badge) · Mehr.
 *   - Advisor (`navRole==='advisor'`): Cockpit · Mandanten · Nachrichten · Mehr;
 *     KEIN FAB (Mandanten-Modus read-only).
 *
 * Der Posteingang-Slot (inkl. `useInboxNewCount`) ist in {@link MobileInboxTab}
 * gekapselt und rendert NUR im Owner-Zweig bei sichtbarem Inbox-Gate — der Hook
 * mountet damit nie im Advisor-Zweig (§2.1 Badge-Mount-Regel).
 *
 * @module components/layout/MobileTabBar
 */

/** Kein Implementierungscode — reine Signatur (§2.1). */
export interface MobileTabBarProps {
    /** dasselbe `navCtx` wie `SidebarNav` (MainLayout). */
    ctx: NavContext;
    /** `'owner' | 'advisor'` (MainLayout: `isAdvisorViewingClient ? 'advisor' : 'owner'`). */
    navRole: NavRole;
    /** triggert `setMobileMenuOpen(true)` — öffnet das „Mehr"-Bottom-Sheet
     * (M-UX-6.1b, `plan-ux-6.1b-bottomsheet.md` §1.3; vormals der Top-Drawer). */
    onOpenMore: () => void;
}

/** Aktiv-Match wie `MainLayout.isActive`: exakt für `/dashboard`, sonst Präfix. */
function isSlotActive(pathname: string, target: string, exact: boolean): boolean {
    if (exact) return pathname === target;
    return pathname === target || pathname.startsWith(`${target}/`);
}

const TAB_BASE_CLASS =
    'fb-compact group relative flex flex-1 flex-col items-center justify-center gap-1 ' +
    'min-w-0 px-1 pt-2 pb-1.5 text-[10px] leading-none tracking-[0.01em] transition-colors ' +
    'duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#20D08A] ' +
    'focus-visible:ring-inset';

const TAB_ACTIVE_CLASS = 'text-[#20D08A]';
const TAB_INACTIVE_CLASS = 'text-muted hover:text-secondary';

/**
 * Icon-Container (32px-Squircle). Trägt im aktiven Zustand eine dezente
 * Mint-Fläche — ein NICHT-farbliches Aktiv-Signal (Form/Fill) zusätzlich zur
 * Hue, damit der aktive Slot auch bei Farbschwäche erkennbar bleibt (D4/D5).
 * Zugleich Positionsanker für das Inbox-Badge.
 */
const TAB_ICON_WRAP =
    'fb-tab-icon relative flex h-8 w-8 items-center justify-center rounded-2xl';

/** Label-Zusatzklasse: aktives Label trägt mehr Gewicht (non-hue-Signal, D5). */
function labelClass(active: boolean): string {
    return `max-w-full truncate ${active ? 'font-semibold' : 'font-medium'}`;
}

/** Ein Icon-über-Label-Tab als Router-Link (echtes `<a>`, §9.4). */
const TabLink: React.FC<{
    to: string;
    exact: boolean;
    icon: React.ReactNode;
    label: string;
    children?: React.ReactNode;
}> = ({ to, exact, icon, label, children }) => {
    const location = useLocation();
    const active = isSlotActive(location.pathname, to, exact);
    return (
        <Link
            to={to}
            aria-current={active ? 'page' : undefined}
            className={`${TAB_BASE_CLASS} ${active ? TAB_ACTIVE_CLASS : TAB_INACTIVE_CLASS}`}
        >
            <span
                className={`${TAB_ICON_WRAP} ${active ? 'bg-[#20D08A]/12' : ''}`}
                aria-hidden="true"
            >
                {icon}
                {children}
            </span>
            <span className={labelClass(active)}>{label}</span>
        </Link>
    );
};

/**
 * Posteingang-Slot mit Badge — gekapselt, damit `useInboxNewCount()` NUR mountet,
 * wenn der Slot tatsächlich rendert (Owner + Inbox-Gate). Rules-of-Hooks-Muster
 * wie `GlobalUnreadBadge` in `MainLayout` (§2.1).
 */
const MobileInboxTab: React.FC<{ to: string; label: string }> = ({ to, label }) => {
    const { t } = useTranslation('common');
    const count = useInboxNewCount();
    const location = useLocation();
    const active = isSlotActive(location.pathname, to, false);
    const badgeAria = count > 0 ? t('mobileTabs.inboxBadgeAria', { count }) : undefined;
    return (
        <Link
            to={to}
            aria-current={active ? 'page' : undefined}
            aria-label={count > 0 ? `${label} — ${badgeAria}` : label}
            className={`${TAB_BASE_CLASS} ${active ? TAB_ACTIVE_CLASS : TAB_INACTIVE_CLASS}`}
        >
            <span
                className={`${TAB_ICON_WRAP} ${active ? 'bg-[#20D08A]/12' : ''}`}
                aria-hidden="true"
            >
                <IconInbox className="h-5 w-5" />
                {count > 0 && (
                    <span
                        className="absolute -top-1 -right-1 flex h-[18px] min-w-[18px] items-center
                                   justify-center rounded-full bg-[#20D08A] px-1 text-[10px]
                                   font-bold leading-none text-[#0C1322] ring-2 ring-[color:var(--surface-base)]"
                    >
                        {count > 99 ? '99+' : count}
                    </span>
                )}
            </span>
            <span className={labelClass(active)}>{label}</span>
        </Link>
    );
};

/**
 * FAB-Bottom-Action-Sheet (§2.3, M-UX-6.1b §1.2): dünner Wrapper um die
 * generische `common/MobileBottomSheet`-Primitive — `role="dialog"`,
 * Fokus-Trap, Body-Scroll-Lock, Backdrop/ESC-Dismiss, Fokus-Return kommen
 * jetzt AUS DER PRIMITIVE (vormals hier dupliziert). Items kommen weiter
 * deklarativ aus `NAV_ACTIONS` (visibility-gefiltert) — kein hartkodiertes
 * Menü (E4). Fokus-Return-Invariante: die FAB fokussiert sich in ihrem
 * `onClick` EXPLIZIT selbst, bevor `sheetOpen` gesetzt wird (§9.4) — ein
 * bloßer Klick fokussiert Buttons weder in Safari noch in jsdom/Jest
 * verlässlich, die Primitive verlässt sich aber genau darauf.
 */
const FabActionSheet: React.FC<{
    ctx: NavContext;
    onClose: () => void;
}> = ({ ctx, onClose }) => {
    const { t } = useTranslation(['common', 'navigation']);
    const navigate = useNavigate();

    const actions = NAV_ACTIONS.filter((action) => action.visibility(ctx) === 'visible');

    const handleSelect = (route: string) => {
        onClose();
        navigate(route);
    };

    return (
        <MobileBottomSheet open onClose={onClose} title={t('mobileTabs.create')}>
            <ul className="px-2 pb-2">
                {actions.map((action) => {
                    const Icon = action.icon;
                    return (
                        <li key={action.id}>
                            <button
                                type="button"
                                onClick={() => handleSelect(action.route)}
                                className="fb-compact fb-sheet-item flex w-full items-center gap-3.5
                                           rounded-2xl px-3 py-3 text-left text-[15px]
                                           font-medium text-primary transition-colors
                                           hover:bg-white/[0.06] focus:outline-none
                                           focus-visible:bg-white/[0.06] active:bg-white/[0.09]"
                            >
                                <span
                                    className="flex h-11 w-11 shrink-0 items-center justify-center
                                               rounded-2xl bg-[#20D08A]/12 text-[17px] text-[#33D998]"
                                    aria-hidden="true"
                                >
                                    <Icon />
                                </span>
                                <span>{t(action.labelKey, { ns: 'navigation' })}</span>
                            </button>
                        </li>
                    );
                })}
            </ul>
        </MobileBottomSheet>
    );
};

const MobileTabBar: React.FC<MobileTabBarProps> = ({ ctx, navRole, onOpenMore }) => {
    const { t } = useTranslation(['common', 'navigation']);
    const [sheetOpen, setSheetOpen] = useState(false);
    const fabRef = useRef<HTMLButtonElement>(null);

    const moreLabel = t('mobileTabs.more');

    if (navRole === 'advisor') {
        const sellerData = ctx.sellerData as { loopBBeta?: boolean } | null | undefined;
        const showCockpit = canUseKanzleiCockpit(ctx.uid, sellerData);
        const showClients = canUseKanzleiCockpit(ctx.uid, sellerData);
        const showMessages = canUseMessaging(ctx.effectiveScopeUid);
        return (
            <nav
                aria-label={t('mobileTabs.barAria')}
                className="fb-mobile-tabbar fixed inset-x-0 bottom-0 z-40 flex items-stretch
                           justify-around bg-[rgb(var(--surface-base-rgb)/0.85)] backdrop-blur-md lg:hidden"
            >
                {showCockpit && (
                    <TabLink
                        to="/dashboard/cockpit"
                        exact={false}
                        icon={<IconPortal className="h-5 w-5" />}
                        label={t('mobileTabs.cockpit')}
                    />
                )}
                {showClients && (
                    <TabLink
                        to="/dashboard/mandanten"
                        exact={false}
                        icon={<IconClients className="h-5 w-5" />}
                        label={t('mobileTabs.clients')}
                    />
                )}
                {showMessages && (
                    <TabLink
                        to="/dashboard/nachrichten"
                        exact={false}
                        icon={<IconMessages className="h-5 w-5" />}
                        label={t('mobileTabs.messages')}
                    />
                )}
                <button
                    type="button"
                    onClick={(event) => {
                        // Fokus-Return-Invariante des Bottom-Sheets (§9.4/M-UX-6.1b
                        // §1.1): der Trigger muss beim Öffnen fokussiert sein — ein
                        // bloßer Klick fokussiert Buttons weder in Safari noch in
                        // jsdom/Jest verlässlich.
                        event.currentTarget.focus();
                        onOpenMore();
                    }}
                    className={`${TAB_BASE_CLASS} ${TAB_INACTIVE_CLASS}`}
                >
                    <span className={TAB_ICON_WRAP} aria-hidden="true">
                        <IconRowActions className="h-5 w-5" />
                    </span>
                    <span className={labelClass(false)}>{moreLabel}</span>
                </button>
            </nav>
        );
    }

    // Owner-Zweig.
    const inboxVisible = UNIFIED_INBOX_ENABLED || canUseInbound(ctx.effectiveScopeUid);
    const inboxRoute = UNIFIED_INBOX_ENABLED ? '/dashboard/eingaenge' : '/dashboard/posteingang';

    return (
        <>
            <nav
                aria-label={t('mobileTabs.barAria')}
                className="fb-mobile-tabbar fixed inset-x-0 bottom-0 z-40 flex items-stretch
                           justify-around bg-[rgb(var(--surface-base-rgb)/0.85)] backdrop-blur-md lg:hidden"
            >
                <TabLink
                    to="/dashboard"
                    exact
                    icon={<IconOverview className="h-5 w-5" />}
                    label={t('mobileTabs.overview')}
                />
                <TabLink
                    to="/dashboard/archiv"
                    exact={false}
                    icon={<IconInvoice className="h-5 w-5" />}
                    label={t('mobileTabs.invoices')}
                />
                <button
                    ref={fabRef}
                    type="button"
                    onClick={(event) => {
                        // Fokus-Return-Invariante (s. „Mehr"-Handler oben + Modul-
                        // Kopf `FabActionSheet`): FAB fokussiert sich explizit selbst.
                        event.currentTarget.focus();
                        setSheetOpen(true);
                    }}
                    aria-haspopup="dialog"
                    aria-expanded={sheetOpen}
                    aria-label={t('mobileTabs.create')}
                    className="fb-compact flex flex-1 items-start justify-center rounded-none
                               focus:outline-none focus-visible:ring-2 focus-visible:ring-[#20D08A]
                               focus-visible:ring-inset"
                >
                    <span
                        className="fb-mobile-fab -mt-6 flex h-14 w-14 items-center justify-center
                                   rounded-full bg-gradient-to-br from-[#38DD9E] to-[#16B074]
                                   text-[#0C1322] ring-4 ring-[color:var(--surface-base)]"
                        aria-hidden="true"
                    >
                        <IconNew
                            className={`h-6 w-6 transition-transform duration-200 ${
                                sheetOpen ? 'rotate-45' : ''
                            }`}
                        />
                    </span>
                </button>
                {inboxVisible && (
                    <MobileInboxTab to={inboxRoute} label={t('mobileTabs.inbox')} />
                )}
                <button
                    type="button"
                    onClick={(event) => {
                        // Fokus-Return-Invariante — s. Kommentar im Advisor-Zweig oben.
                        event.currentTarget.focus();
                        onOpenMore();
                    }}
                    className={`${TAB_BASE_CLASS} ${TAB_INACTIVE_CLASS}`}
                >
                    <span className={TAB_ICON_WRAP} aria-hidden="true">
                        <IconRowActions className="h-5 w-5" />
                    </span>
                    <span className={labelClass(false)}>{moreLabel}</span>
                </button>
            </nav>
            {sheetOpen && <FabActionSheet ctx={ctx} onClose={() => setSheetOpen(false)} />}
        </>
    );
};

export default MobileTabBar;
